import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { useTranslation } from "react-i18next";
import { Hand } from "lucide-react";
import initOpenCascade from "occt-import-js";

let _occtPromise = null;
function getOcct() {
  if (!_occtPromise) {
    _occtPromise = initOpenCascade({
      locateFile: (file) => (file.endsWith(".wasm") ? "/occt-import-js.wasm" : file),
    });
  }
  return _occtPromise;
}

/* Draggable + auto-rotating STEP model for the landing hero — Swiss light styling. */
export default function HeroStepSpinner({ src = "/models/hero-part.step" }) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const [hintVisible, setHintVisible] = useState(true);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let disposed = false;
    let frame = null;
    let resumeTimer = null;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf3f4f6);

    scene.add(new THREE.AmbientLight(0xffffff, 0.85));
    const key = new THREE.DirectionalLight(0xffffff, 0.9);
    key.position.set(1, 1.2, 1);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xffe4d6, 0.35);
    fill.position.set(-1, 0.4, -0.8);
    scene.add(fill);

    const camera = new THREE.PerspectiveCamera(32, container.clientWidth / container.clientHeight, 0.1, 5000);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(container.clientWidth, container.clientHeight);
    container.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.enablePan = false;
    controls.enableZoom = false;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 2.2;
    controls.target.set(0, 0, 0);
    controls.addEventListener("start", () => {
      controls.autoRotate = false;
      if (resumeTimer) clearTimeout(resumeTimer);
      renderer.domElement.style.cursor = "grabbing";
      setHintVisible(false);
    });
    controls.addEventListener("end", () => {
      renderer.domElement.style.cursor = "grab";
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => { controls.autoRotate = true; }, 2500);
    });
    renderer.domElement.style.cursor = "grab";
    renderer.domElement.style.touchAction = "none";

    const spinGroup = new THREE.Group();
    scene.add(spinGroup);

    (async () => {
      try {
        const resp = await fetch(src);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const buf = new Uint8Array(await resp.arrayBuffer());
        const occt = await getOcct();
        const result = occt.ReadStepFile(buf, null);
        if (!result?.success || disposed) throw new Error("STEP parse failed");

        const inner = new THREE.Group();
        result.meshes.forEach((m) => {
          const geom = new THREE.BufferGeometry();
          geom.setAttribute("position", new THREE.BufferAttribute(new Float32Array(m.attributes.position.array), 3));
          if (m.attributes.normal) {
            geom.setAttribute("normal", new THREE.BufferAttribute(new Float32Array(m.attributes.normal.array), 3));
          } else {
            geom.computeVertexNormals();
          }
          if (m.index?.array) {
            geom.setIndex(new THREE.BufferAttribute(new Uint32Array(m.index.array), 1));
          }
          const mesh = new THREE.Mesh(geom, new THREE.MeshPhongMaterial({ color: 0xb9c2cc, shininess: 70, side: THREE.DoubleSide }));
          inner.add(mesh);
          const edges = new THREE.LineSegments(
            new THREE.EdgesGeometry(geom, 18),
            new THREE.LineBasicMaterial({ color: 0xff4500, transparent: true, opacity: 0.55 })
          );
          inner.add(edges);
        });
        inner.rotation.x = -Math.PI / 2;
        // center the model inside the spin group
        const box = new THREE.Box3().setFromObject(inner);
        const center = new THREE.Vector3();
        box.getCenter(center);
        inner.position.sub(center);
        spinGroup.add(inner);

        const size = new THREE.Vector3();
        box.getSize(size);
        const maxDim = Math.max(size.x, size.y, size.z);
        const dist = (maxDim / 2) / Math.tan((camera.fov * Math.PI) / 360) * 1.12;
        camera.position.set(dist * 0.75, dist * 0.55, dist * 0.75);
        camera.lookAt(0, 0, 0);
        controls.update();
        setStatus("ready");
      } catch (e) {
        console.error("Hero STEP load failed:", e);
        if (!disposed) setStatus("error");
      }
    })();

    const tick = () => {
      frame = requestAnimationFrame(tick);
      controls.update();
      renderer.render(scene, camera);
    };
    tick();

    const onResize = () => {
      if (!container) return;
      camera.aspect = container.clientWidth / container.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(container.clientWidth, container.clientHeight);
    };
    window.addEventListener("resize", onResize);

    return () => {
      disposed = true;
      window.removeEventListener("resize", onResize);
      if (frame) cancelAnimationFrame(frame);
      if (resumeTimer) clearTimeout(resumeTimer);
      controls.dispose();
      spinGroup.traverse((o) => {
        if (o.geometry) o.geometry.dispose();
        if (o.material) o.material.dispose();
      });
      renderer.dispose();
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
    };
  }, [src]);

  return (
    <div className="relative w-full h-full" data-testid="hero-step-viewer">
      <div ref={containerRef} className="absolute inset-0" data-testid="hero-step-canvas" />
      {status === "ready" && hintVisible && (
        <div className="absolute bottom-3 left-3 flex items-center gap-1.5 font-mono text-[9px] tracking-[0.15em] uppercase text-cyan-300 bg-slate-950/70 border border-cyan-500/30 px-2 py-1 rounded-sm pointer-events-none animate-pulse" data-testid="hero-drag-hint">
          <Hand size={11}/> {t("landing.dragHint")}
        </div>
      )}
      {status === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-100 pointer-events-none">
          <span className="font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase animate-pulse">// PARSING STEP · OCCT WASM…</span>
        </div>
      )}
      {status === "error" && (
        <img src="/images/hero_animation_poster.jpg" alt={t("landing.heroAlt")} className="absolute inset-0 w-full h-full object-cover" />
      )}
    </div>
  );
}
